import React, { useCallback, useEffect, useState } from "react";
import { createEmptyImage } from "domains/canvas/components/LayerPanel/createEmptyImage";
import { InferenceBuilderErrors } from "domains/canvas/hooks/useInferenceBuilderErrors";
import { disabledOnWheel } from "domains/forms/disabledOnWheel";
import { SDXL_BASE_MODELS } from "domains/inference/constants/baseModel";
import { SCHEDULERS } from "domains/inference/constants/Schedulers";
import { useInferenceCreateContext } from "domains/inference/contexts/InferenceProvider";
import { LaunchShortcut } from "domains/shortcuts/components/Shortcut";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import ButtonWithCuIndicator from "domains/ui/components/ButtonWithCuIndicator";
import Divider from "domains/ui/components/Divider";
import ScenarioInput from "domains/ui/components/ScenarioInput";
import Select from "domains/ui/components/Select";
import SidebarTitle from "domains/ui/components/Sidebar/SidebarTitle";
import Slider from "domains/ui/components/Slider";
import WithLabelAndTooltip from "domains/ui/components/WithLabelAndTooltip";
import { FormattedMessage } from "react-intl";

import { Flex, Image, VStack } from "@chakra-ui/react";

import AlertErrors from "./AlertErrors";
import Section from "./Section";
import SectionConcepts from "./SectionConcepts";
import SectionNegativePrompt from "./SectionNegativePrompt";
import SectionPrompt from "./SectionPrompt";
import SectionReferenceImage from "./SectionReferenceImage";
import SelectControlNet from "./SelectControlNet";
import SelectDimensions from "./SelectDimensions";
import SelectInferenceType from "./SelectInferenceType";
import SelectModel from "./SelectModel";
import SliderControlEnd from "./SliderControlEnd";
import SliderInfluence from "./SliderInfluence";
export interface InferenceGeneratorSidebarProps {
  onInferenceRequest: () => Promise<void> | void;
  isLoading: boolean;
  isDisabled: boolean;
  generateButtonLabel?: string;
  generateButtonDisabledTooltip?: string;
  errors?: InferenceBuilderErrors;
}

export default function InferenceGeneratorSidebar({
  onInferenceRequest,
  isLoading,
  isDisabled,
  generateButtonLabel,
  generateButtonDisabledTooltip,
  errors,
}: InferenceGeneratorSidebarProps) {
  const {
    isFlux,
    isFluxPro1_1,
    isGptImage,
    isModelWithBase,

    isAbleToGenerate,
    isImportingAsset,
    nbImages,
    setNbImages,
    samplingSteps,
    setSamplingSteps,

    samplingStepsMin,
    samplingStepsMax,
    guidance,
    setGuidance,
    seed,
    setSeed,
    model,
    setModel,
    isLoadingModel,
    prompt,
    setPrompt,
    negativePrompt,
    setNegativePrompt,
    referenceImages,
    mask,
    scheduler,
    setScheduler,

    cuCost,
    isCuLoading,

    isInCanvas,
    isTexture,
    canHaveReference,

    maxNbImages,
    guidanceMax,
    guidanceMin,

    inferenceType,

    baseModelId,
    setBaseModelId,
  } = useInferenceCreateContext();
  const { maxBatchStandard, subscription } = usePlanContext();
  const planSamplingStepsLimit =
    subscription &&
    (subscription?.plan === "free" || subscription?.plan === "cu-creator")
      ? 30
      : undefined;

  const [selectModelModalIsOpen, setSelectModelModalIsOpen] =
    useState<boolean>(false);
  const [isInferenceRequested, setIsInferenceRequested] =
    useState<boolean>(false);

  const hasError =
    errors && Object.values(errors).some((error) => error?.type === "error");
  const isModelWithConceptComposition = model?.concepts?.length ?? false;

  // ----------------------------------

  const requestInference = useCallback(() => {
    if (isDisabled || hasError || !isAbleToGenerate) {
      return;
    }

    if (!model) {
      setSelectModelModalIsOpen(true);
      return;
    }

    setIsInferenceRequested(true);
  }, [model, isDisabled, hasError, isAbleToGenerate]);

  // ----------------------------------

  useEffect(() => {
    if (!isInferenceRequested) return;
    setIsInferenceRequested(false);
    setTimeout(onInferenceRequest, 1);
  }, [onInferenceRequest, setIsInferenceRequested, isInferenceRequested]);

  const isGenerateButtonDisabled = isDisabled || hasError || !isAbleToGenerate;

  const handleSeedChange = useCallback(
    (value: string) => {
      setSeed(value);
      if (value) {
        setNbImages(1);
      }
    },
    [setSeed, setNbImages]
  );

  const handleImageCountChange = useCallback(
    (value: number) => {
      setNbImages(value);
      if (value > 1) {
        setSeed("");
      }
    },
    [setNbImages, setSeed]
  );

  return (
    <>
      {!isInCanvas && !isTexture && (
        <>
          <SidebarTitle
            title={
              <FormattedMessage
                id="domains.inference.inferenceGenerator.title"
                defaultMessage="Generate Images"
              />
            }
            guide="inference-intro"
            // videoUrl="" // TODO
            // videoTooltip="" // TODO
          />
          <Divider />
        </>
      )}

      <SelectModel
        isOpen={selectModelModalIsOpen}
        setIsOpen={setSelectModelModalIsOpen}
        model={model}
        setModel={setModel}
        isLoadingModel={isLoadingModel}
        variant={isInCanvas ? "canvas" : isTexture ? "texture" : "image"}
      />
      {!isInCanvas && <Divider />}

      {isModelWithConceptComposition && (
        <>
          <SectionConcepts />
          <Divider />
        </>
      )}

      <SectionPrompt
        id="prompt"
        compact={isInCanvas}
        prompt={prompt}
        setPrompt={setPrompt}
        getModel={async () => model}
        guide="inference-parameter-prompts"
        placeholder={
          isTexture
            ? "Describe your texture, and let prompt tools generate, complete, or translate it."
            : undefined
        }
      />
      <Divider />

      {errors && <AlertErrors errors={errors} />}
      <Section compact={isInCanvas}>
        <VStack align="stretch">
          <ButtonWithCuIndicator
            onClick={requestInference}
            isLoading={isLoading}
            isDisabled={isGenerateButtonDisabled}
            data-testid="inference-builder-generate-image-button"
            cuCost={cuCost}
            isCuLoading={isCuLoading}
            w="100%"
            tooltip={
              isGenerateButtonDisabled ? (
                generateButtonDisabledTooltip
              ) : (
                <LaunchShortcut />
              )
            }
            tooltipProps={{
              placement: "right",
              hasArrow: true,
              isDisabled:
                isGenerateButtonDisabled && !generateButtonDisabledTooltip,
            }}
          >
            {generateButtonLabel ?? (
              <FormattedMessage
                id="domains.inference.inferenceGenerator.generate"
                defaultMessage="Generate"
              />
            )}
          </ButtonWithCuIndicator>
        </VStack>
      </Section>
      <Divider />

      {!isFlux && !isGptImage && (
        <>
          <SectionNegativePrompt
            id="negativePrompt"
            compact={isInCanvas}
            loading={isImportingAsset}
            prompt={negativePrompt}
            setPrompt={setNegativePrompt}
            guide="inference-parameter-prompts"
          />
          <Divider />
        </>
      )}

      {/* ------------------------- */}

      {!isInCanvas && canHaveReference && (
        <>
          <SectionReferenceImage />
          <Divider />
        </>
      )}
      {isInCanvas && inferenceType !== "txt2img" && (
        <>
          <Section
            compact
            headerProps={{
              title: mask ? "Reference & Mask" : "Reference",
              id: "referenceImage",
              noCollapse: true,
            }}
          >
            <VStack>
              <Flex
                pos="relative"
                align="center"
                justify="center"
                w="100%"
                h="160px"
              >
                <Image
                  className="checkerboard"
                  pos="absolute"
                  maxW="100%"
                  maxH="160px"
                  borderRadius="md"
                  alt="Reference Image"
                  data-testid="canvas-inference-builder-reference-image-alt-area"
                  src={
                    referenceImages.reference?.url ?? createEmptyImage(160, 160)
                  }
                />
                {mask && (
                  <Image
                    pos="absolute"
                    maxW="100%"
                    maxH="160px"
                    alt="mask"
                    src={mask}
                  />
                )}
              </Flex>
              <SelectInferenceType />
              <SelectControlNet />
              <SliderInfluence type="reference" />
              <SliderControlEnd />
            </VStack>
          </Section>
          <Divider />
        </>
      )}

      <Section
        compact={isInCanvas}
        headerProps={{
          title: "Settings",
          id: "settings",
          guide: "inference-parameter-others",
        }}
      >
        <VStack align="stretch">
          <WithLabelAndTooltip
            label={isTexture ? "Texture count" : "Image count"}
            premiumTooltip={
              maxBatchStandard < 16 && maxNbImages > maxBatchStandard
                ? "Upgrade to generate larger batches (up to 16 images)"
                : undefined
            }
          >
            <Slider
              withNumberInput
              data-testid="inference-builder-image-number-slider"
              max={Math.min(maxBatchStandard, maxNbImages)}
              min={1}
              onChange={handleImageCountChange}
              step={1}
              value={nbImages}
            />
          </WithLabelAndTooltip>

          {!isFluxPro1_1 && !isGptImage && (
            <>
              <WithLabelAndTooltip
                label="Sampling Steps"
                tooltip="Sampling steps balance image detail and processing time in AI models."
                premiumTooltip={
                  planSamplingStepsLimit !== undefined &&
                  planSamplingStepsLimit < samplingStepsMax
                    ? "Upgrade to use more sampling steps"
                    : undefined
                }
              >
                <Slider
                  withNumberInput
                  data-testid="inference-builder-sampling-step-slider"
                  max={Math.min(
                    samplingStepsMax,
                    planSamplingStepsLimit ?? samplingStepsMax
                  )}
                  min={samplingStepsMin}
                  onChange={setSamplingSteps}
                  step={samplingStepsMax === 150 ? 5 : 1}
                  value={samplingSteps}
                />
              </WithLabelAndTooltip>

              <WithLabelAndTooltip
                label="Guidance"
                tooltip="Higher values will keep images closer to the prompt"
              >
                <Slider
                  withNumberInput
                  data-testid="inference-builder-guidance-slider"
                  max={guidanceMax}
                  min={guidanceMin}
                  onChange={setGuidance}
                  step={0.1}
                  value={guidance}
                />
              </WithLabelAndTooltip>
            </>
          )}

          {!isInCanvas && !isTexture && <SelectDimensions />}

          {isModelWithBase && (
            <WithLabelAndTooltip label="Base Model">
              <Select
                w="fit-content"
                mr={-1}
                ml="auto"
                onChange={(e) => setBaseModelId(e.target.value)}
                value={baseModelId}
              >
                {SDXL_BASE_MODELS.map((model) => (
                  <option key={model.id} value={model.id} label={model.name} />
                ))}
              </Select>
            </WithLabelAndTooltip>
          )}

          {!isFlux && !isGptImage && (
            <WithLabelAndTooltip
              label="Scheduler"
              tooltip="Schedulers create slight adjustments to the image, and users may prefer results of one scheduler to another."
            >
              <Select
                w="fit-content"
                mr={-1}
                ml="auto"
                onChange={(e) => {
                  const value = e.target.value as typeof scheduler | "default";
                  setScheduler(value === "default" ? undefined : value);
                }}
                value={scheduler ?? "default"}
              >
                <option value="default" label="Default (optimal)" />
                {Object.entries(SCHEDULERS).map(([key, value]) => (
                  <option key={key} value={key} label={value} />
                ))}
              </Select>
            </WithLabelAndTooltip>
          )}

          {!isGptImage && (
            <WithLabelAndTooltip
              label="Seed"
              tooltip="Use a custom seed for targeted results (single-image inference)"
            >
              <ScenarioInput
                value={seed}
                setValue={handleSeedChange}
                placeholder="Random"
                type="number"
                onWheel={disabledOnWheel}
                dataTestId="inference-builder-seed"
              />
            </WithLabelAndTooltip>
          )}
        </VStack>
      </Section>
      <Divider />
    </>
  );
}
